import React, { useCallback, useId, useMemo } from "react"

import { Tooltip } from "@coval/ui"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Customized,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import type { ChartBarFilter } from "@/types/ChartBarFilter"

export interface HorizontalBarSegment {
  label: string
  value: number
  color: string
  percent?: string // Pre-calculated percentage (if not provided, calculated from total)
  striped?: boolean // When true, use diagonal stripes pattern instead of solid fill
  rawValue?: string // Original unformatted value (used for filter matching when label is display-formatted)
}

interface HorizontalStackedBarChartProps {
  segments: HorizontalBarSegment[]
  height?: number
  onBarClick?: (filter: { type: "exact"; value: string }) => void
  activeFilters?: ChartBarFilter[]
}

interface BarLabelItem {
  value: number
  percent: string
}

const CustomLabels = ({
  formattedGraphicalItems,
  chartData,
}: {
  formattedGraphicalItems?: Array<{
    props?: {
      data?: Array<{
        x?: number
        y?: number
        width?: number
        height?: number
        value?: number
        payload?: { name: string; value: number; percent: string }
      }>
    }
  }>
  chartData: BarLabelItem[]
}) => {
  const barData = formattedGraphicalItems?.[0]?.props?.data

  if (!barData) {
    return null
  }

  return (
    <g>
      {barData.map((entry, index) => {
        const item = chartData[index]
        if (!entry || !item) {
          return null
        }

        const x = (entry.x ?? 0) + (entry.width ?? 0) + 6
        const y = (entry.y ?? 0) + (entry.height ?? 0) / 2

        return (
          <text
            key={`label-${index}`}
            x={x}
            y={y}
            fill="var(--coval-text-muted)"
            fontSize={10}
            dominantBaseline="middle"
          >
            {item.value} ({item.percent}%)
          </text>
        )
      })}
    </g>
  )
}

const StackedBarYAxisTick = ({
  x,
  y,
  payload,
}: {
  x?: number
  y?: number
  payload?: { value: string }
}) => {
  const maxLength = 10
  const fullText = payload?.value ?? ""
  const isTruncated = fullText.length > maxLength
  const displayText = isTruncated
    ? `${fullText.slice(0, maxLength - 1)}…`
    : fullText

  if (isTruncated) {
    return (
      <foreignObject
        x={(x ?? 0) - 80}
        y={(y ?? 0) - 10}
        width={80}
        height={20}
        style={{ overflow: "visible" }}
      >
        <Tooltip content={fullText} side="top">
          <div
            className="text-right"
            style={{
              fontSize: 12,
              color: "var(--coval-text-muted)",
              lineHeight: "20px",
            }}
          >
            {displayText}
          </div>
        </Tooltip>
      </foreignObject>
    )
  }

  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fontSize={12}
      fill="var(--coval-text-muted)"
    >
      {displayText}
    </text>
  )
}

/**
 * A horizontal bar chart component using recharts.
 * Displays categorical data as horizontal bars with labels on the left
 * and count/percentage on the right of each bar.
 */
export const HorizontalStackedBarChart: React.FC<
  HorizontalStackedBarChartProps
> = ({ segments, height, onBarClick, activeFilters }) => {
  // Calculate total and prepare data for recharts
  const { chartData, total, maxValue } = useMemo(() => {
    const total = segments.reduce((sum, seg) => sum + seg.value, 0)
    const maxValue = Math.max(...segments.map((seg) => seg.value), 1)

    // Each segment becomes its own row
    // Use pre-calculated percentage if provided, otherwise calculate from total
    const chartData = segments.map((seg) => ({
      name: seg.label,
      value: seg.value,
      color: seg.color,
      striped: seg.striped ?? false,
      percent:
        seg.percent !== undefined
          ? seg.percent
          : total > 0
            ? ((seg.value / total) * 100).toFixed(0)
            : "0",
    }))

    return { chartData, total, maxValue }
  }, [segments])

  // Check if any segments need striped pattern
  const hasStripedSegments = segments.some((seg) => seg.striped)

  // Generate unique ID for SVG pattern to avoid collisions across chart instances
  const patternId = useId()
  const stripedPatternId = `diagonal-stripes-${patternId}`

  // Use same height as vertical distribution charts (164px)
  const chartHeight = height ?? 164

  // Calculate nice X-axis domain and ticks with consistent scale
  const { xAxisMax, xAxisTicks } = useMemo(() => {
    // Determine a nice step size based on max value
    let step: number
    if (maxValue <= 4) {
      step = 1
    } else if (maxValue <= 10) {
      step = 2
    } else if (maxValue <= 25) {
      step = 5
    } else if (maxValue <= 50) {
      step = 10
    } else {
      step = Math.ceil(maxValue / 5 / 10) * 10
    }

    // Calculate max as a multiple of step, with some padding
    const xAxisMax = Math.ceil((maxValue + step) / step) * step

    // Generate ticks at regular intervals
    const ticks: number[] = []
    for (let i = 0; i <= xAxisMax; i += step) {
      ticks.push(i)
    }

    return { xAxisMax, xAxisTicks: ticks }
  }, [maxValue])

  // Build a lookup from chartData index to the raw (unformatted) value for filter matching.
  // Falls back to segment.label when rawValue is not provided.
  const rawValues = useMemo(
    () => segments.map((seg) => seg.rawValue ?? seg.label),
    [segments],
  )

  // Exact filter values (used to detect "has any exact filter" even if value is outside visible bars)
  const exactFilterValues = useMemo(
    () =>
      new Set(
        (activeFilters ?? [])
          .filter(
            (f): f is Extract<ChartBarFilter, { type: "exact" }> =>
              f.type === "exact",
          )
          .map((f) => f.value),
      ),
    [activeFilters],
  )

  // Determine which bars are active based on activeFilters
  const activeIndices = useMemo(() => {
    const indices = new Set<number>()
    rawValues.forEach((rawValue, index) => {
      if (exactFilterValues.has(rawValue)) {
        indices.add(index)
      }
    })
    return indices
  }, [exactFilterValues, rawValues])

  const handleBarClick = useCallback(
    (index: number) => {
      if (!onBarClick) {
        return
      }
      const raw = rawValues[index]
      if (raw == null) {
        return
      }
      onBarClick({ type: "exact", value: raw })
    },
    [onBarClick, rawValues],
  )

  if (chartData.length === 0 || total === 0) {
    return null
  }

  return (
    <div className="w-full overflow-visible" style={{ height: chartHeight }}>
      {/* SVG pattern definition for striped bars */}
      {hasStripedSegments && (
        <svg width="0" height="0" style={{ position: "absolute" }}>
          <defs>
            <pattern
              id={stripedPatternId}
              patternUnits="userSpaceOnUse"
              width="3"
              height="3"
              patternTransform="rotate(45)"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="3"
                stroke="var(--coval-text-muted)"
                strokeWidth="1.5"
              />
            </pattern>
          </defs>
        </svg>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 0, left: 0, bottom: 10 }}
          barCategoryGap="20%"
        >
          <CartesianGrid
            horizontal={false}
            vertical
            stroke="var(--coval-border)"
            strokeDasharray="none"
          />
          <XAxis
            type="number"
            domain={[0, xAxisMax]}
            axisLine={{ stroke: "var(--coval-border)" }}
            tickLine={false}
            tick={{ fontSize: 11, fill: "var(--coval-text-muted)" }}
            ticks={xAxisTicks}
            height={30}
            label={{
              value: "Transcripts",
              position: "insideBottom",
              offset: -5,
              style: {
                fontSize: 11,
                fill: "var(--coval-text)",
                fontWeight: 600,
              },
            }}
          />
          <YAxis
            type="category"
            dataKey="name"
            axisLine={false}
            tickLine={false}
            width={85}
            tick={<StackedBarYAxisTick />}
          />
          <Bar
            dataKey="value"
            radius={[0, 2, 2, 0]}
            barSize={16}
            cursor={onBarClick ? "pointer" : undefined}
            onClick={(_data: unknown, index: number) => handleBarClick(index)}
            role={onBarClick ? "group" : undefined}
            aria-label={
              onBarClick ? "Clickable chart bars for filtering" : undefined
            }
          >
            {chartData.map((entry, index) => {
              const isActive = activeIndices.has(index)
              const isDimmed = exactFilterValues.size > 0 && !isActive
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.striped ? `url(#${stripedPatternId})` : entry.color
                  }
                  fillOpacity={isDimmed ? 0.3 : 1}
                />
              )
            })}
          </Bar>
          <Customized component={CustomLabels} chartData={chartData} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
